// src/components/GST/VendorMatchPopup.js
import React, { useState } from "react";
import { Modal, Table, Button, Badge } from "react-bootstrap";

/* ================= styles ================= */

const compactCell = {
  padding: "1px 4px",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const stickyHeader = {
  position: "sticky",
  top: 0,
  background: "#f8f9fa",
  zIndex: 2,
};

const diffStyle = { color: "#d10000" };

/* ================= helpers (SAME AS MATCHRESULTSGRID) ================= */

const isStrDiff = (a, b) =>
  (a || "").trim().toUpperCase() !== (b || "").trim().toUpperCase();

const isDateDiff = (a, b) => (a || "") !== (b || "");

const isNumDiffTol = (a, b, tol) =>
  Math.abs(Number(a || 0) - Number(b || 0)) > tol;

const f2 = (v) => Number(v || 0).toFixed(2);

const badgeColor = (type) => {
  const t = type?.toUpperCase() || "";
  if (t === "EXACT") return "success";
  if (t.includes("GROUP")) return "primary";
  if (t.includes("FUZZY") || t.includes("RELAXED")) return "warning";
  if (t.includes("ONLY IN GSTR")) return "danger";
  if (t.includes("ONLY IN ERP")) return "secondary";
  return "dark";
};

/* ================= component ================= */

const VendorMatchPopup = ({ show, onClose, vendor, data = [] }) => {
  const [filterMode, setFilterMode] = useState("ALL"); // ALL | DIFF | MATCHED
  const [tolerance, setTolerance] = useState(0);

  /* ---------- filtering ---------- */

  const filteredData = data.filter((row) => {
    const partyDiff = isStrDiff(row.PARTY_NAME_2B, row.PARTY_NAME_ERP);
    const billNoDiff = isStrDiff(row.BILL_NO_2B, row.BILL_NO_ERP);
    const billDtDiff = isDateDiff(row.BILL_DATE_2B, row.BILL_DATE_ERP);

    const taxableDiff = isNumDiffTol(row.TAXABLE_2B, row.ASSESSABLE_ERP, tolerance);
    const igstDiff = isNumDiffTol(row.IGST_2B, row.IGST_ERP, tolerance);
    const cgstDiff = isNumDiffTol(row.CGST_2B, row.CGST_ERP, tolerance);
    const sgstDiff = isNumDiffTol(row.SGST_2B, row.SGST_ERP, tolerance);
    const cessDiff = isNumDiffTol(row.CESS_2B, row.CESS_ERP, tolerance);

    const hasDiff =
      partyDiff ||
      billNoDiff ||
      billDtDiff ||
      taxableDiff ||
      igstDiff ||
      cgstDiff ||
      sgstDiff ||
      cessDiff;

    if (filterMode === "DIFF") return hasDiff;
    if (filterMode === "MATCHED") return !hasDiff;
    return true;
  });

  /* ================= render ================= */

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      backdrop="static"
      keyboard
      centered
    >
      {/* ===== Header ===== */}
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.8rem" }}>
          Vendor Match Details —{" "}
          <b>{vendor?.PARTY_NAME_ERP}</b>{" "}
          <span style={{ fontSize: "0.7rem", color: "#666" }}>
            ({vendor?.GSTIN})
          </span>
        </Modal.Title>
      </Modal.Header>

      {/* ===== Control Bar ===== */}
      <Modal.Body style={{ padding: "6px" }}>
        <div
          style={{
            marginBottom: "4px",
            padding: "3px 6px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            background: "#f8f9fa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "0.62rem",
          }}
        >
          {/* LEFT */}
          <div>
            <b>{filteredData.length}</b> records
          </div>

          {/* RIGHT */}
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span>Tol:</span>
            <Button
              size="sm"
              variant="outline-secondary"
              style={{ fontSize: "0.6rem", padding: "0 4px" }}
              onClick={() => setTolerance((t) => Math.max(0, t - 1))}
            >
              ←
            </Button>

            <span style={{ minWidth: "12px", textAlign: "center" }}>
              {tolerance}
            </span>

            <Button
              size="sm"
              variant="outline-secondary"
              style={{ fontSize: "0.6rem", padding: "0 4px" }}
              onClick={() => setTolerance((t) => Math.min(10, t + 1))}
            >
              →
            </Button>

            <Button
              size="sm"
              variant={filterMode === "ALL" ? "secondary" : "outline-secondary"}
              style={{ fontSize: "0.6rem", padding: "1px 6px" }}
              onClick={() => setFilterMode("ALL")}
            >
              All
            </Button>

            <Button
              size="sm"
              variant={filterMode === "DIFF" ? "danger" : "outline-danger"}
              style={{ fontSize: "0.6rem", padding: "1px 6px" }}
              onClick={() => setFilterMode("DIFF")}
            >
              Diff
            </Button>

            <Button
              size="sm"
              variant={filterMode === "MATCHED" ? "success" : "outline-success"}
              style={{ fontSize: "0.6rem", padding: "1px 6px" }}
              onClick={() => setFilterMode("MATCHED")}
            >
              Matched
            </Button>
          </div>
        </div>

        {/* ===== Grid ===== */}
        <div
          style={{
            maxHeight: "65vh",
            overflow: "auto",
            border: "1px solid #ddd",
            borderRadius: "4px",
          }}
        >
          <Table bordered hover size="sm" style={{ fontSize: "0.62rem", margin: 0 }}>
            <thead>
              <tr>
                <th style={{ ...compactCell, ...stickyHeader }}>Type</th>
                <th style={{ ...compactCell, ...stickyHeader }}>2B Inv</th>
                <th style={{ ...compactCell, ...stickyHeader }}>ERP Inv</th>
                <th style={{ ...compactCell, ...stickyHeader }}>2B Dt</th>
                <th style={{ ...compactCell, ...stickyHeader }}>ERP Dt</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>Taxable 2B</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>Assess ERP</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>IGST 2B</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>IGST ERP</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CGST 2B</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CGST ERP</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>SGST 2B</th>
                <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>SGST ERP</th>
                <th style={{ ...compactCell, ...stickyHeader }}>Score</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((row, idx) => {
                const taxableDiff = isNumDiffTol(row.TAXABLE_2B, row.ASSESSABLE_ERP, tolerance);
                const igstDiff = isNumDiffTol(row.IGST_2B, row.IGST_ERP, tolerance);
                const cgstDiff = isNumDiffTol(row.CGST_2B, row.CGST_ERP, tolerance);
                const sgstDiff = isNumDiffTol(row.SGST_2B, row.SGST_ERP, tolerance);

                return (
                  <tr key={idx} style={{ opacity: row.APPROVED === "Y" ? 0.45 : 1 }}>
                    <td style={compactCell}>
                      <Badge bg={badgeColor(row.MATCH_TYPE)} style={{ fontSize: "0.6rem" }}>
                        {row.MATCH_TYPE}
                      </Badge>
                    </td>

                    <td style={compactCell}>{row.BILL_NO_2B}</td>
                    <td style={compactCell}>{row.BILL_NO_ERP}</td>
                    <td style={compactCell}>{row.BILL_DATE_2B}</td>
                    <td style={compactCell}>{row.BILL_DATE_ERP}</td>

                    <td className="text-end" style={{ ...compactCell, ...(taxableDiff ? diffStyle : {}) }}>
                      {f2(row.TAXABLE_2B)}
                    </td>
                    <td className="text-end" style={{ ...compactCell, ...(taxableDiff ? diffStyle : {}) }}>
                      {f2(row.ASSESSABLE_ERP)}
                    </td>

                    <td className="text-end" style={{ ...compactCell, ...(igstDiff ? diffStyle : {}) }}>
                      {f2(row.IGST_2B)}
                    </td>
                    <td className="text-end" style={{ ...compactCell, ...(igstDiff ? diffStyle : {}) }}>
                      {f2(row.IGST_ERP)}
                    </td>

                    <td className="text-end" style={{ ...compactCell, ...(cgstDiff ? diffStyle : {}) }}>
                      {f2(row.CGST_2B)}
                    </td>
                    <td className="text-end" style={{ ...compactCell, ...(cgstDiff ? diffStyle : {}) }}>
                      {f2(row.CGST_ERP)}
                    </td>

                    <td className="text-end" style={{ ...compactCell, ...(sgstDiff ? diffStyle : {}) }}>
                      {f2(row.SGST_2B)}
                    </td>
                    <td className="text-end" style={{ ...compactCell, ...(sgstDiff ? diffStyle : {}) }}>
                      {f2(row.SGST_ERP)}
                    </td>

                    <td style={compactCell}>
                      <Badge bg="info" style={{ fontSize: "0.6rem" }}>
                        {row.MATCH_SCORE}%
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Modal.Body>

      {/* ===== Footer ===== */}
      <Modal.Footer style={{ padding: "4px" }}>
        <Button
          size="sm"
          variant="secondary"
          style={{ fontSize: "0.65rem" }}
          onClick={onClose}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VendorMatchPopup;
